import { Alignment, DisplayType, HorizontalAlignment, NullDateRangeDateBehavior, ObjectUtil } from "@mcleod/core";
import { ComponentPropDefinitionUtil, ComponentPropDefinitions } from "../../base/ComponentProps";
import { PropType } from "../../base/PropType";
import { LabelPropDefinitions } from "../label/LabelProps";
import { PanelPropDefinitions, PanelProps } from "../panel/PanelProps";
import { DropdownItem } from "../textbox/DropdownItem";
import { TextboxPropDefinitions } from "../textbox/TextboxProps";

export enum EndDateType {
    DAYS_OUT = "daysOut",
    DATE = "date"
}

export interface DateRangeFilterProps extends PanelProps {
    nullStartDateBehavior: NullDateRangeDateBehavior
    nullEndDateBehavior: NullDateRangeDateBehavior;
    endDateType: EndDateType;
    dateRangeLabelAlign: Alignment;
}

export class DateRangeFilterPropDefinitions {
    private static dateRangeFilterPropDefs: ComponentPropDefinitions;
    private static dateTextboxPropDefs: ComponentPropDefinitions;
    private static startDateTextboxPropDefs: ComponentPropDefinitions;
    private static endDateTextboxPropDefs: ComponentPropDefinitions;
    private static daysOutTextboxPropDefs: ComponentPropDefinitions;
    private static dateRangeTextboxPropDefs: ComponentPropDefinitions;

    public static getDefinitions(): ComponentPropDefinitions {
        if (this.dateRangeFilterPropDefs == null) {
            this.dateRangeFilterPropDefs = {
                ...PanelPropDefinitions.cloneDefs(),
                endDateType: {
                    type: PropType.string,
                    defaultValue: EndDateType.DATE,
                    dropdownProps:  { items: Object.values(EndDateType) },
                    visibleInMcLeodTailor: true,
                    editableInMcLeodTailor: { baseComponent: false, customComponent: true },
                    description: "This controls the input type for the end date. 'Days Out' will allow the user to select a number of days from the start date. 'Date' will allow the user to select a specific end date."
                },
                nullStartDateBehavior: {
                    type: PropType.string,
                    defaultValue: NullDateRangeDateBehavior.UNBOUND,
                    dropdownProps: { items: this.getNullDateBehaviorDropdownItems(true) },
                    visibleInMcLeodTailor: true,
                    editableInMcLeodTailor: { baseComponent: false, customComponent: true },
                    description: "This controls how the inner components are validated and the format of the dateRange label text. This property does not control what's displayed or saved by the start textbox.\n\n'Unbound' allows null start dates to pass validation. DateRange Label format: 'Starting mm/dd/yy'."
                },
                nullEndDateBehavior: {
                    type: PropType.string,
                    defaultValue: NullDateRangeDateBehavior.UNBOUND,
                    dropdownProps: { items: this.getNullDateBehaviorDropdownItems(false) },
                    visibleInMcLeodTailor: true,
                    editableInMcLeodTailor: { baseComponent: false, customComponent: true },
                    description: "This controls how the inner components are validated and the format of the dateRange label text. This property does not control what's displayed or saved by the end textbox.\n\n'Unbound' allows null end dates to pass validation. DateRange Label format: 'Through mm/dd/yy'."
                },
                dateRangeLabelAlign: {
                    type: PropType.string,
                    defaultValue: HorizontalAlignment.RIGHT,
                    dropdownProps: { items: [Alignment.RIGHT, Alignment.BOTTOM] },
                    visibleInMcLeodTailor: true,
                    editableInMcLeodTailor: { baseComponent: false, customComponent: true },
                    description: "This controls the position of the date range label relative to the date textboxes."
                }
            };

            ComponentPropDefinitionUtil.populateComponentPropNames(this.dateRangeFilterPropDefs);

            for (const propDef of Object.values(this.dateRangeFilterPropDefs)) {
                if (propDef.category === "Data") {
                    propDef.visibleInDesigner = false;
                } else if (propDef.name?.startsWith("enabledDuring")) {
                    propDef.editableInDesigner = false;
                }
            }

        }

        return this.dateRangeFilterPropDefs;
    }

    private static getNullDateBehaviorDropdownItems(forStartDate: boolean): DropdownItem[] {
        const items: DropdownItem[] = [];
        for (const value of Object.values(NullDateRangeDateBehavior)) {
            const display = this.getNullDateRangeDateBehaviorDisplay(value, forStartDate);
            items.push(new DropdownItem(value, display));
        }
        return items;
    }

    private static getNullDateRangeDateBehaviorDisplay(value: NullDateRangeDateBehavior, forStartDate: boolean): string {
        switch (value) {
            case NullDateRangeDateBehavior.USE_CURRENT_DATE:
                return "use current date";
            case NullDateRangeDateBehavior.USE_OTHER_DATE:
                return forStartDate ? `use end date` :  "use start date";
            case NullDateRangeDateBehavior.UNBOUND:
                return "unbound";
        }
    }

    public static getDateTextboxDefinitions(): ComponentPropDefinitions {
        if (this.dateTextboxPropDefs == null) {
            this.dateTextboxPropDefs = ObjectUtil.deepCopy(TextboxPropDefinitions.getDefinitions());
            for (const propDef of Object.values(this.dateTextboxPropDefs)) {
                if (propDef.category === "Lookup model" || propDef.name === "saveParamMode") {
                    propDef.visibleInDesigner = false;
                }
                if (propDef.name === "id" ||  propDef.name === "saveParamMode") {
                    propDef.editableInDesigner = false;
                }
            }
            this.dateTextboxPropDefs.displayType.dropdownProps = {items: [DisplayType.DATE, DisplayType.DATETIME, DisplayType.TIME]};
        }
        return this.dateTextboxPropDefs;
    }

    public static getStartDateTextboxDefinitions(): ComponentPropDefinitions {
        if (this.startDateTextboxPropDefs == null) {
            this.startDateTextboxPropDefs = ObjectUtil.deepCopy(this.getDateTextboxDefinitions());
        }
        return this.startDateTextboxPropDefs;
    }

    public static getEndDateTextboxDefinitions(): ComponentPropDefinitions {
        if (this.endDateTextboxPropDefs == null) {
            this.endDateTextboxPropDefs = ObjectUtil.deepCopy(this.getDateTextboxDefinitions());
        }
        return this.endDateTextboxPropDefs;
    }

    public static getDaysOutTextboxDefinitions(): ComponentPropDefinitions {
        if (this.daysOutTextboxPropDefs == null) {
            this.daysOutTextboxPropDefs = ObjectUtil.deepCopy(TextboxPropDefinitions.getDefinitions());
            for (const propDef of Object.values(this.daysOutTextboxPropDefs)) {
                if (propDef.category === "Lookup model") {
                    propDef.visibleInDesigner = false;
                }
                if (propDef.name === "id" || propDef.name === "displayType" || propDef.name === "minValue") {
                    propDef.editableInDesigner = false;
                }
            }
        }
        return this.daysOutTextboxPropDefs;
    }

    public static getDateRangeLabelDefinitions(): ComponentPropDefinitions {
        if (this.dateRangeTextboxPropDefs == null) {
            this.dateRangeTextboxPropDefs = ObjectUtil.deepCopy(LabelPropDefinitions.getDefinitions());

            for (const propDef of Object.values(this.dateRangeTextboxPropDefs)) {
                if (propDef.category === "Data") {
                    propDef.visibleInDesigner = false;
                }
                if (propDef.name === "id") {
                    propDef.editableInDesigner = false;
                }
            }
        }
        return this.dateRangeTextboxPropDefs;
    }
}
